import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { createApiKey, deleteApiKey, getApiKeys } from "sj-ui-toolkit/src/api/admin/settings";
import { getApiTokensClient } from "sj-ui-toolkit/src/client/svcApiTokens";
import { randomString } from "sj-ui-toolkit/src/utils";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

const REPORTING_KEY_LENGTH = 32;
const REPORTING_ENTITLEMENTS = ["reporting_api"];

type ReportingKey = { id: string; name: string };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ReportingApiConnection = () => {
  useBreadcrumb({
    label: "Reporting API",
    route: AdminAppRoutes.routes.connections.routes.reportingApi.route,
    display: "page",
  });

  const { useMigratedApiKeys } = useFlags();

  const toast = useRef<Toast>(null);
  const apiTokensClient = getApiTokensClient();
  const [apiKeys, setApiKeys] = useState<ReportingKey[]>([]);
  const [newKeyValue, setNewKeyValue] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [keyName, setKeyName] = useState("");

  const showLocalToast = useCallback(
    (
      severity: "success" | "info" | "warn" | "error",
      summary: string,
      detail: string,
      life = 3000
    ) => {
      toast.current?.show({ severity, summary, detail, life });
    },
    []
  );

  const loadKeys = useCallback(async () => {
    setIsLoading(true);
    try {
      if (useMigratedApiKeys) {
        const { data, error } =
          await apiTokensClient.listApiTokensWithEntitlements(REPORTING_ENTITLEMENTS);
        if (error) throw error;
        setApiKeys(
          (data?.apiTokens ?? [])
            .filter(token => token.uuid)
            .map(token => ({ id: token.uuid!, name: token.label || token.uuid! }))
        );
      } else {
        const data = await getApiKeys();
        setApiKeys((data?.apiKeys ?? []).map(key => ({ id: String(key.id), name: key.name })));
      }
    } catch {
      showLocalToast("error", "Error", "Failed to load Reporting API keys.", 5000);
    } finally {
      setIsLoading(false);
    }
  }, [showLocalToast, useMigratedApiKeys, apiTokensClient]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const sortedKeys = useMemo(
    () => [...apiKeys].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [apiKeys]
  );

  const handleGenerate = async () => {
    if (!keyName.trim()) return;

    setIsLoading(true);
    try {
      if (useMigratedApiKeys) {
        const { data, error } = await apiTokensClient.issueApiToken(
          keyName.trim(),
          REPORTING_ENTITLEMENTS
        );
        if (error) throw error;
        setNewKeyValue(data?.secret ?? "");
        await loadKeys();
      } else {
        const generated = randomString(REPORTING_KEY_LENGTH);
        const data = await createApiKey(keyName.trim(), generated);
        if (!data) throw new Error("Failed to create API key");
        setApiKeys(prev => [...prev, { id: String(data.apiKey.id), name: data.apiKey.name }]);
        setNewKeyValue(generated);
      }
      setKeyName("");
      setIsCreateModalVisible(false);
      showLocalToast("success", "Success", "Reporting API key created successfully");
    } catch {
      showLocalToast("error", "Error", "Failed to create Reporting API key. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = (text: string) => {
    if (text) {
      navigator.clipboard.writeText(text);
      showLocalToast("info", "Copied", "API key copied to clipboard");
    }
  };

  const confirmDelete = (apiKey: ReportingKey) => {
    confirmDialog({
      message: `Are you sure you want to delete "${apiKey.name}"? This action cannot be undone.`,
      header: "Delete Reporting API Key",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          if (useMigratedApiKeys) {
            const { error } = await apiTokensClient.revokeApiToken(apiKey.id);
            if (error) throw error;
          } else {
            await deleteApiKey(Number(apiKey.id));
          }
          setApiKeys(prev => prev.filter(k => k.id !== apiKey.id));
          showLocalToast("info", "Deleted", "Reporting API key deleted successfully");
        } catch {
          showLocalToast("error", "Error", "Failed to delete Reporting API key");
        }
      },
    });
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="mb-4">
        <h1>Reporting API</h1>
      </div>

      <Card>
        <div className="flex justify-between items-center">
          <div>
            <strong>Reporting API Keys</strong>
            <p className="m-0 text-secondary">Create and manage keys for reporting integrations.</p>
          </div>
          <Button
            label="Create Reporting API Key"
            icon="pi pi-plus"
            onClick={() => setIsCreateModalVisible(true)}
            disabled={isLoading}
            data-testid="reporting-api-add-key-button"
          />
        </div>

        {newKeyValue && (
          <div className="mt-4">
            <strong>New Reporting API Key (copy this now - it will not be shown again):</strong>
            <div className="p-inputgroup mt-2">
              <InputText value={newKeyValue} readOnly data-testid="reporting-api-new-key-value" />
              <Button
                icon="pi pi-copy"
                onClick={() => handleCopy(newKeyValue)}
                tooltip="Copy to clipboard"
                tooltipOptions={{ position: "top", className: "whitespace-nowrap" }}
              />
              <Button
                icon="pi pi-times"
                onClick={() => setNewKeyValue("")}
                className="p-button-secondary"
                tooltip="Dismiss"
                tooltipOptions={{ position: "top", className: "whitespace-nowrap" }}
              />
            </div>
          </div>
        )}

        <div className="mt-4">
          <DataTable
            value={sortedKeys}
            className="p-datatable-sm"
            tableClassName="dt-responsive-text"
            sortMode="single"
            loading={isLoading}
          >
            <Column field="name" header="Name" sortable />
            <Column
              header="Action"
              body={(rowData: ReportingKey) => (
                <Button
                  label="Delete"
                  severity="danger"
                  text
                  size="small"
                  disabled={isLoading}
                  onClick={() => confirmDelete(rowData)}
                />
              )}
            />
          </DataTable>
          {!isLoading && sortedKeys.length === 0 && (
            <p className="text-secondary mt-3">
              No Reporting API keys found. Create one to get started.
            </p>
          )}
        </div>
      </Card>

      <Dialog
        header="Create Reporting API Key"
        visible={isCreateModalVisible}
        onHide={() => {
          setIsCreateModalVisible(false);
          setKeyName("");
        }}
        style={{ width: "40rem", maxWidth: "95vw" }}
        modal
        pt={{ root: { "data-testid": "reporting-api-create-key-dialog" } }}
        footer={
          <div className="flex gap-2 justify-end">
            <Button
              label="Cancel"
              className="p-button-secondary"
              onClick={() => {
                setIsCreateModalVisible(false);
                setKeyName("");
              }}
              disabled={isLoading}
            />
            <Button
              label="Create"
              icon="pi pi-check"
              onClick={handleGenerate}
              disabled={isLoading || !keyName.trim()}
              data-testid="reporting-api-key-create-button"
            />
          </div>
        }
      >
        <InputText
          value={keyName}
          onChange={e => setKeyName(e.target.value)}
          placeholder="Key Name (e.g., Reporting Dashboard)"
          className="w-full"
          maxLength={120}
          disabled={isLoading}
          autoFocus
          data-testid="reporting-api-key-name-input"
        />
      </Dialog>
    </>
  );
};

export default ReportingApiConnection;
